import React, { useState, useEffect, useRef } from 'react';
import { Search, User, Phone, MapPin, X } from 'lucide-react';
import MainService from '../services/mainservice';

const CustomerAutoComplete = ({ 
  value, 
  onChange, 
  onCustomerSelect,
  onInvalidateCustomerIds,
  placeholder = "Search customer name...",
  className = ""
}) => {
  const [searchTerm, setSearchTerm] = useState(value || '');
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);

  // Load customers from API
  const loadCustomers = async () => {
    try {
      setIsLoading(true);
      
      const tokenData = localStorage.getItem('AINSOFT_DRIVER_TOKEN');
      if (!tokenData) {
        return;
      }

      const userData = JSON.parse(tokenData);
      const response = await MainService.CustomersInGridInPortal(userData.ClientId);
      
      if (response && response.length > 0) {
        setCustomers(response);
      }
    } catch (error) {
      console.error('Error loading customers:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCustomers();
  }, []);

  // Filter customers based on search term
  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredCustomers([]);
      return;
    }

    const filtered = customers.filter(customer => 
      (customer.Name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (customer.MobileNo1 || '').includes(searchTerm) ||
      (customer.MobileNo2 || '').includes(searchTerm) ||
      (customer.EmailId || '').toLowerCase().includes(searchTerm.toLowerCase())
    );

    setFilteredCustomers(filtered);
  }, [searchTerm, customers]);

  // Handle input change
  const handleInputChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    onChange(value);
    setIsOpen(value.trim() !== '');
    setSelectedCustomer(null);
    if (onInvalidateCustomerIds) {
      onInvalidateCustomerIds();
    }
  };

  // Handle customer selection
  const handleCustomerSelect = (customer) => {
    setSelectedCustomer(customer);
    setSearchTerm(customer.Name || '');
    setIsOpen(false);
    onChange(customer.Name || '');
    
    // Call the onCustomerSelect callback with full customer data
    if (onCustomerSelect) {
      onCustomerSelect(customer);
    }
  };

  // Handle clear selection
  const handleClear = () => {
    setSearchTerm('');
    setSelectedCustomer(null);
    onChange('');
    setIsOpen(false);
    if (onCustomerSelect) {
      onCustomerSelect(null);
    }
    inputRef.current?.focus();
  };

  // Handle input focus
  const handleFocus = () => {
    if (searchTerm.trim() !== '') {
      setIsOpen(true);
    }
  };

  // Handle click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  // Handle keyboard navigation
  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setIsOpen(false);
    }
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <Search className="h-4 w-4 text-gray-400" />
        </div>
        <input
          ref={inputRef}
          type="text"
          value={searchTerm}
          onChange={handleInputChange}
          onFocus={handleFocus}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
          autoComplete="off"
        />
        {searchTerm && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
          >
            <X className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded-lg shadow-lg max-h-60 overflow-auto">
          {isLoading ? (
            <div className="p-4 text-center text-gray-500">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600 mx-auto mb-2"></div>
              Loading customers...
            </div>
          ) : filteredCustomers.length === 0 ? (
            <div className="p-4 text-center text-gray-500">
              {searchTerm.trim() === '' ? 'Start typing to search customers...' : 'No customers found'}
            </div>
          ) : (
            <div className="py-1">
              {filteredCustomers.map((customer, index) => (
                <button
                  key={customer.Id || index}
                  type="button"
                  onClick={() => handleCustomerSelect(customer)}
                  className="w-full px-4 py-3 text-left hover:bg-gray-50 focus:bg-gray-50 focus:outline-none border-b border-gray-100 last:border-b-0"
                >
                  <div className="flex items-start space-x-3">
                    <div className="flex-shrink-0">
                      <div className="w-8 h-8 bg-gradient-to-br from-green-100 to-green-200 rounded-full flex items-center justify-center">
                        <span className="text-xs font-semibold text-green-700">
                          {(customer.Name || '').split(' ').map(n => n[0]).join('').toUpperCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center space-x-2">
                        <User className="h-4 w-4 text-gray-400" />
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {customer.Name || 'N/A'}
                        </p>
                      </div>
                      <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                        {customer.MobileNo1 && (
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3" />
                            <span>{customer.MobileNo1}</span>
                          </div>
                        )}
                        {customer.MobileNo2 && customer.MobileNo2 !== customer.MobileNo1 && (
                          <div className="flex items-center space-x-1">
                            <Phone className="h-3 w-3" />
                            <span>{customer.MobileNo2}</span>
                          </div>
                        )}
                        {customer.EmailId && (
                          <div className="flex items-center space-x-1">
                            <span>@</span>
                            <span className="truncate">{customer.EmailId}</span>
                          </div>
                        )}
                      </div>
                      {customer.Address && (
                        <div className="mt-1 flex items-center space-x-1 text-xs text-gray-500">
                          <MapPin className="h-3 w-3" />
                          <span className="truncate">{customer.Address}</span>
                        </div>
                      )}
                      {(customer.LocationName || customer.EmirateName) && (
                        <div className="mt-1 text-xs text-gray-500">
                          <span>{customer.LocationName || ''}</span>
                          {customer.LocationName && customer.EmirateName && ', '}
                          <span>{customer.EmirateName || ''}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CustomerAutoComplete;
